import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import TranslateIcon from "@material-ui/icons/Translate";
import TuneSharpIcon from "@material-ui/icons/TuneSharp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Alert } from "@material-ui/lab";
import { withStyles } from "@material-ui/styles";
import { Component, Fragment, useState } from "react";
import { toast } from "react-toastify";
import FormEdit from "../components/form-element/FormEdit";
import FormDialog from "../components/FormDialog";
import { Loader } from "../components/Loader";
import { CLOSE_POPUP } from "../constants/Constants";
import { fetchLanguages } from "../services/api-service";
import FormBuilderService from "../services/form-builder.service";
import {
  FORM_STATUS,
  FormStore,
  dialogState,
  formState,
  loaderState,
  publishedFormState,
  sideTocState,
} from "../services/form.store";
import Titlebar from "../shared/component/Titlebar";
import {
  checkIfLatest,
  dataSorter,
  htmlBase64Utils,
  sanitizeTitle,
} from "../utility/CommonUtils";
import { IS_CONDITIONAL_FORM_ENABLED, SORT_KEY } from "../utility/Constant";
import EditFormMetaData from "./EditFormMetaData";
import EditConditionalForm from "./EditConditionalForm";

const styles = (theme) => ({
  root: {
    padding: "12px",
    paddingBottom: "80px",
  },
  flexGrow: {
    flexGrow: 1,
  },
  appBar: {
    top: "auto",
    bottom: 0,
  },
  toolBar: {
    backgroundColor: "#242D3E",
    bottom: 0,
    top: "auto",
    position: "relative",
    width: "100%",
    padding: 20,
  },
  draftButton: {
    border: "1px solid white",
    color: "white",
  },
  previewButton: {
    color: "white",
  },
  formVersionAlert: {
    backgroundColor: "#FFFCEA",
    border: "1px solid #E9C928",
    borderLeft: "8px solid #E9C928",
    margin: "8px 10px 0 10px",
    color: "black",
    "& .MuiAlert-icon": {
      color: "#E9C928",
    },
  },
});

const textFieldStyles = makeStyles((theme) => ({
  textField: {
    "& .MuiInputLabel-shrink": {
      marginTop: 10,
      marginLeft: -20,
    },
    marginLeft: "auto",
    marginRight: "auto",
    paddingBottom: 0,
    paddingLeft: 0,
    fontWeight: 500,
    marginTop: -10,
  },
  editHierarchyBtn: {
    marginLeft: "16px",
  },
}));

class EditForm extends Component {
  constructor() {
    super();
    this.state = {
      form: {},
      translatedForm: {},
      loading: true,
      showDialog: false,
      showTOC: false,
      showSlide: false,
      languages: [],
      selectedLanguage: "",
      showPopup: false,
      defaultTabName: "",
      enableSaveBtn: true,
      isButtonDisabled: false,
      isFormLatest: true,
      showVersionDialog: false,
    };
    this.formBuilderService = new FormBuilderService();
  }

  async checkForLatestForm() {
    const { formCode } = this.state.form;
    const { data } = await this.formBuilderService.getLatestFormVersion(
      formCode
    );
    const isVersionLatest = checkIfLatest(this.state.form, data);
    if (!isVersionLatest) {
      this.setState({ showVersionDialog: true });
      FormStore.sharedInstance().editPublishedForm(false);
      FormStore.sharedInstance().onLoading(false);
    } else {
      FormStore.sharedInstance().editPublishedForm(true);
      FormStore.sharedInstance().onLoading(false);
    }
  }

  componentDidMount() {
    FormStore.sharedInstance().resetFormDetails();
    FormStore.sharedInstance().initializeForm(FormStore.sharedInstance().formDetails);
    const formId = this.props.match.params.id;
    this.formSubscription = formState.subscribe((response) => {
      htmlBase64Utils(response, false);
      if (
        response.defaultLanguageCode !== undefined &&
        response.defaultLanguageCode !== null
      ) {
        this.setState({
          form: {
            ...response,
            translationFromCode: response.defaultLanguageCode,
          },
        });
      } else {
        this.setState({
          form: { ...response, translationFromCode: "en" },
        });
      }
    });
    this.loaderSubscription = loaderState.subscribe((isLoading) => {
      this.setState({ loading: isLoading });
    });
    this.publishedFormSubscription = publishedFormState.subscribe(
      (isLastUpdatedVersion) => {
        this.setState({
          enableSaveBtn: isLastUpdatedVersion,
          isFormLatest: isLastUpdatedVersion,
        });
      }
    );
    this.dialogSubscription = dialogState.subscribe((value) => {
      this.setState({ showDialog: value });
    });
    this.sideTocState = sideTocState.subscribe((value) => {
      if (value) {
        this.setState({
          showSlide: !value,
          showTOC: value,
        });
      }
    });
    FormStore.sharedInstance().load(formId);
    fetchLanguages().then((response) => {
      this.setState({
        languages: dataSorter(response, SORT_KEY.NAME),
      });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    let currentId = this.state.form.id;
    if (prevState.form.id && prevState.form.id !== currentId) {
      this.props.history.push("/editForm/" + currentId);
    } else if (prevState.form.id !== currentId) {
      this.checkForLatestForm();
    }
  }

  componentWillUnmount() {
    this.formSubscription.unsubscribe();
    this.loaderSubscription.unsubscribe();
    this.dialogSubscription.unsubscribe();
    this.sideTocState.unsubscribe();
  }

  saveAsDraft = () => {
    this.handleSave(FORM_STATUS.DRAFT);
  };

  publishForm = () => {
    this.handleSave(FORM_STATUS.PUBLISHED);
  };

  handleSave = (status) => {
    this.setState({ isButtonDisabled: true });
    FormStore.sharedInstance().updateFormStatus(status);
    let errorIds = FormStore.sharedInstance().getErrorIds();
    if (errorIds.length !== 0) {
      const actionText =
        status === FORM_STATUS.PUBLISHED ? "published" : "saved";
      toast.error(
        `Form could not be ${actionText} due to errors in your form.`
      );
      this.setState({ isButtonDisabled: false });
    } else {
      FormStore.sharedInstance()
        .saveForm()
        .then((res) => {
          this.setState({ isButtonDisabled: false });
          if (status === FORM_STATUS.PUBLISHED && !res.error) {
            this.props.history.push(
              "/existingForms"
            ); /* Removed setTImeout function that reduced the performance to 2 seconds */
          }
        })
        .catch(() => {
          this.setState({ isButtonDisabled: false });
        });
    }
  };

  isDisabled = () => {
    return this.state.form.formStatus === FORM_STATUS.PUBLISHED;
  };

  updateFormMetaData = () => {
    this.setState({ showDialog: true });
  };

  handleCancel = () => {
    this.setState({ showDialog: false });
  };

  handleEditView = (open) => (event) => {
    this.setState({
      showTOC: true,
      showSlide: false,
    });
  };

  toggleDrawer = (open) => (event) => {
    this.setState({
      showSlide: open,
      showTOC: !open,
    });
  };

  languageHandler = (event, key) => {
    const transationCodeModify = key.key?.includes(".$")
      ? key.key.replace(/^.\$/, "")
      : key.key;
    if (event) {
      this.setState({ loading: true });
      this.setState(
        {
          form: {
            ...this.state.form,
            translationToCode: transationCodeModify,
          },
          selectedLanguage: event.target.value,
          translationCode: transationCodeModify,
        },
        () => {
          if (
            this.state.form.translationFromCode &&
            this.state.form.translationToCode
          ) {
            const payload = {
              ...this.state.form,
              title: sanitizeTitle(
                this.state.form.title,
                this.state.form.translationFromCode
              ),
            };
            this.formBuilderService.getTranslatedForm(payload).then((res) => {
              if (res.data) {
                this.setState({
                  translatedForm: res.data,
                  defaultTabName: this.state.languages
                    .filter((language) =>
                      this.state.form.translationFromCode.includes(
                        language.languageCode
                      )
                    )
                    .map((language) => language.name),
                  showPopup: true,
                });
                FormStore.sharedInstance().sendTranslateForm(
                  res.data,
                  this.state.form.translationToCode
                );
              } else {
                console.log("data", res.data);
              }
              this.loaderSubscription = loaderState.subscribe((isLoading) => {
                this.setState({ loading: isLoading });
              });
            });
          }
        }
      );
    }
  };

  closeDrawer = () => {
    this.setState({ showSlide: false });
  };

  handleClose = (flag) => {
    this.setState({ showPopup: false });
    if (flag !== CLOSE_POPUP) {
      this.setState({ selectedLanguage: "" });
    }
  };

  handleFormVersionCancel = () => {
    this.props.history.push("/existingForms");
  };

  render() {
    const { classes } = this.props;
    const { loading, isFormLatest } = this.state;
    return (
      <Fragment>
        <FormTitle
          title={this.state.form.title}
          isFormLatest={isFormLatest}
          updateFormMetaData={this.updateFormMetaData}
          slideInOut={this.toggleDrawer}
          showToc={this.state.showTOC}
          handleEditView={this.handleEditView}
          selectedLanguage={this.state.selectedLanguage}
          showLanguages={this.languageHandler}
          languages={this.state.languages}
          form={this.state.form}
          translateIcon={<TranslateIcon />}
          className={classes}
          isLoading={this.state.loading}
        />
        {!loading && !isFormLatest && (
          <Alert
            variant="filled"
            severity="info"
            className={classes.formVersionAlert}
          >
            Note: This version is outdated and cannot be modified. It is
            available for reference only.
          </Alert>
        )}
        <Typography component="div" className={classes.root}>
          <Card square>
            <RenderForm
              showPopup={this.state.showPopup}
              selectedLanguage={this.state.selectedLanguage}
              defaultTabName={this.state.defaultTabName}
              form={this.state.form}
              translatedForm={this.state.translatedForm}
              handleClose={this.handleClose}
              showTOC={this.state.showTOC}
              showSlide={this.state.showSlide}
              toggleDrawer={this.toggleDrawer}
              drawerPaper={classes.drawerPaper}
              closeDrawer={this.closeDrawer}
            />
          </Card>
          <div style={{ textAlign: "center" }}>
            <Loader loading={this.state.loading} />
          </div>
        </Typography>
        {isFormLatest && (
          <AppBar className={classes.appBar}>
            <Toolbar className={classes.toolBar}>
              <Button
                className={classes.previewButton}
                color="inherit"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  console.log("Preview button");
                }}
              >
                Preview
              </Button>
              <span className={classes.flexGrow} />
              <Button
                className={classes.draftButton}
                variant="outlined"
                color="inherit"
                disableElevation
                disabled={
                  this.state.isButtonDisabled || this.state.loading
                    ? true
                    : !this.state.enableSaveBtn
                }
                onClick={this.saveAsDraft}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={
                  this.state.isButtonDisabled || this.state.loading
                    ? true
                    : this.isDisabled()
                }
                onClick={this.publishForm}
              >
                Publish
              </Button>
            </Toolbar>
          </AppBar>
        )}
        <EditFormMetaData
          open={this.state.showDialog}
          onClose={this.handleCancel}
        />
        <FormDialog
          open={this.state.showVersionDialog}
          handleConfirm={() => this.setState({ showVersionDialog: false })}
          handleCancel={() => this.handleFormVersionCancel()}
        />
      </Fragment>
    );
  }
}

function RenderForm(props) {
  return props.form.children !== undefined ? (
    <FormEdit
      showPopup={props.showPopup}
      selectedLanguage={props.selectedLanguage}
      defaultTabName={props.defaultTabName}
      form={props.form}
      translatedForm={props.translatedForm}
      handleClose={props.handleClose}
      showTOC={props.showTOC}
      showSlide={props.showSlide}
      toggleDrawer={props.toggleDrawer}
      closeDrawer={props.closeDrawer}
      drawerPaper={props.drawerPaper}
    />
  ) : (
    <div></div>
  );
}

function FormTitle(props) {
  const [showEditHierarchy, setShowEditHierarchy] = useState(false);
  const classes = textFieldStyles();
  const title = props.title !== undefined ? props.title : "";

  return (
    <Titlebar
      title={title}
      size="small"
      color="primary"
      variant="outlined"
      editBtn={
        props.isFormLatest && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={props.updateFormMetaData}
              disabled={props.isLoading}
            >
              Edit Metadata
            </Button>
            {IS_CONDITIONAL_FORM_ENABLED && (<Button
              variant="outlined"
              color="primary"
              onClick={() => setShowEditHierarchy(true)}
              disabled={props.isLoading}
              className={classes.editHierarchyBtn}
            >
              Edit Hierarchy
            </Button>)}
            {showEditHierarchy && (
              <EditConditionalForm
                showEditHierarchy={showEditHierarchy}
                setShowEditHierarchy={setShowEditHierarchy}
              />
            )}
          </>
        )
      }
    >
      <Box
        display="flex"
        justifyContent="space-between"
        bgcolor="background.paper"
      >
        <Box>
          <Grid>
            <TextField
              label={props.translateIcon}
              select
              fullWidth
              InputProps={{ disableUnderline: true }}
              value={props.selectedLanguage}
              className={classes.textField}
              onChange={props.showLanguages}
              disabled={!props.isFormLatest}
            >
              {props.languages.map((option) => (
                <MenuItem key={option.languageCode} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Box>
        <Box>
          <IconButton onClick={props.slideInOut(true)}>
            <TuneSharpIcon />
          </IconButton>
        </Box>
        <Box>
          <IconButton onClick={props.handleEditView(true)}>
            <FormatListNumberedIcon
              color={props.showToc ? "primary" : "inherit"}
            />
          </IconButton>
        </Box>
      </Box>
    </Titlebar>
  );
}

export default withStyles(styles, { withTheme: true })(EditForm);
